import type { GeneratorFailedEntry } from '../../model/use-agent-panel-store';
import { AlertTriangle, XCircle } from 'lucide-react';
import { useCallback } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import { Alert, Badge, Button, Text } from '@/shared/ui';
import { useAgentPanelStore } from '../../model/use-agent-panel-store';
import { useGeneratorCommit } from '../../model/use-generator-commit';
import css from './partial-failure-step.module.scss';

const styles = pickCss(css, css.root);

const slotLabel: Record<GeneratorFailedEntry['entry']['slot'], string> = {
  morning: 'Morning',
  lunch: 'Lunch',
  afternoon: 'Afternoon',
  dinner: 'Dinner',
};

// 412 (locked) is retryable once the lock clears; 404 (missing product or
// version) is permanent and the consultant should drop it from the batch.
// Everything else (5xx, transient) → treat as retryable so the consultant
// can try again rather than be forced to skip.
const isPermanent = (code: string | null): boolean => code === '404';

const summaryAlertTitle = (
  failed: ReadonlyArray<GeneratorFailedEntry>
): string => {
  // Pull the first reason as the alert title so the user gets an
  // actionable message instead of a generic "some items failed".
  const reason = failed[0]?.reason;
  return reason ?? 'Some items could not be added';
};

// Renders frame `pf-root`. Skip drops the failed batch and advances to
// `done`; Retry re-runs the commit pipeline for `failed[]` only — the
// already-succeeded items stay in the planner untouched.
export const PartialFailureStep = () => {
  const generatorState = useAgentPanelStore((s) => s.generatorState);
  const setGeneratorState = useAgentPanelStore((s) => s.setGeneratorState);
  const commit = useGeneratorCommit();
  const isPartialFailure = generatorState?.phase === 'partial-failure';

  const handleSkipRemaining = useCallback(() => {
    if (!isPartialFailure) return;
    setGeneratorState({ phase: 'done', total: generatorState.succeeded });
  }, [isPartialFailure, generatorState, setGeneratorState]);

  const handleRetryRemaining = useCallback(() => {
    if (!isPartialFailure) return;
    void commit(
      generatorState.failed.map((f) => f.entry),
      generatorState.versionId
    );
  }, [isPartialFailure, generatorState, commit]);

  if (!isPartialFailure) return null;

  const { total, succeeded, failed } = generatorState;
  const summary = `${succeeded} of ${total} added · ${failed.length} to retry`;

  return (
    <div className={styles()}>
      <Alert variant="warning">
        <span className={css.alertBlock}>
          <Text size="sm" weight="semibold">
            {summaryAlertTitle(failed)}
          </Text>
          <Text size="xs" variant="muted">
            {summary}
          </Text>
        </span>
      </Alert>

      <Text size="sm" weight="semibold">
        Failed items · {failed.length}
      </Text>

      <ul aria-label="Failed items" className={css.failedList}>
        {failed.map((failure, idx) => {
          const permanent = isPermanent(failure.code);
          return (
            <li
              className={css.failedRow}
              data-permanent={permanent ? 'true' : undefined}
              key={`${failure.entry.dayIndex}-${failure.entry.slot}-${idx}`}
            >
              <span aria-hidden="true" className={css.icon}>
                {permanent ? (
                  <XCircle size={16} />
                ) : (
                  <AlertTriangle size={16} />
                )}
              </span>
              <span className={css.body}>
                <Text size="sm" weight="medium">
                  {slotLabel[failure.entry.slot]} · Day{' '}
                  {failure.entry.dayIndex + 1}
                </Text>
                <Text size="xs" variant="muted">
                  {failure.reason}
                </Text>
              </span>
              {failure.code && <Badge variant="error">{failure.code}</Badge>}
            </li>
          );
        })}
      </ul>

      <footer className={css.footer}>
        <Button
          onClick={handleSkipRemaining}
          size="default"
          type="button"
          variant="outline"
        >
          Skip remaining
        </Button>
        <Button
          onClick={handleRetryRemaining}
          size="default"
          type="button"
          variant="primary"
        >
          Retry remaining
        </Button>
      </footer>
    </div>
  );
};
